import React, { useState, useEffect } from 'react';
import { useUser } from '../context/UserContext';
import api from '../services/api';

const IndiqueGanhePage = ({ onVoltar }) => {
  const { user, token } = useUser();
  const [codigoConvite, setCodigoConvite] = useState('');
  const [gerandoCodigo, setGerandoCodigo] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [indicacoes, setIndicacoes] = useState([]);
  const [totalIndicacoes, setTotalIndicacoes] = useState(0);
  const [alert, setAlert] = useState({ show: false, message: '', type: '' });

  useEffect(() => {
    const fetchDados = async () => {
      try {
        setLoading(true);
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        };

        // Obter o código de convite do usuário
        const response = await api.get('/api/users/invite-code', config);
        setCodigoConvite(response.data.codigoConvite || '');

        // Obter as indicações aprovadas
        const indicacoesResponse = await api.get('/api/users/indicacoes', config);
        setIndicacoes(indicacoesResponse.data.indicacoes || []);
        setTotalIndicacoes(indicacoesResponse.data.totalIndicacoes || 0);
      } catch (err) {
        setAlert({
          show: true,
          message: 'Erro ao carregar dados. Tente novamente mais tarde.',
          type: 'error'
        });

        // Auto-hide the alert after 3 seconds
        setTimeout(() => {
          setAlert({ show: false, message: '', type: '' });
        }, 3000);

        console.error('Erro ao carregar dados:', err);
      } finally {
        setLoading(false);
      }
    };

    if (token) {
      fetchDados();
    }
  }, [token]);

  const copiarCodigo = () => {
    navigator.clipboard.writeText(codigoConvite);
    setAlert({
      show: true,
      message: 'Código de convite copiado para a área de transferência!',
      type: 'success'
    });

    // Auto-hide the alert after 3 seconds
    setTimeout(() => {
      setAlert({ show: false, message: '', type: '' });
    }, 3000);
  };

  const gerarCodigoConvite = async () => {
    if (gerandoCodigo) return;

    try {
      setGerandoCodigo(true);
      setError('');

      const config = {
        headers: { Authorization: `Bearer ${token}` }
      };

      const response = await api.post('/api/users/generate-invite-code', {}, config);

      if (response.data.success) {
        setCodigoConvite(response.data.codigoConvite);
        setAlert({
          show: true,
          message: 'Código de convite gerado com sucesso!',
          type: 'success'
        });

        // Auto-hide the alert after 3 seconds
        setTimeout(() => {
          setAlert({ show: false, message: '', type: '' });
        }, 3000);
      }
    } catch (err) {
      setAlert({
        show: true,
        message: err.response?.data?.message || 'Erro ao gerar código de convite. Tente novamente.',
        type: 'error'
      });

      // Auto-hide the alert after 3 seconds
      setTimeout(() => {
        setAlert({ show: false, message: '', type: '' });
      }, 3000);

      console.error('Erro ao gerar código de convite:', err);
    } finally {
      setGerandoCodigo(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-violet-50 via-purple-50 to-indigo-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-32 w-32 border-b-2 border-violet-600 mx-auto mb-4"></div>
          <p className="text-gray-600 text-lg">Carregando dados de indicações...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F8F6FC]">
      <div className="container mx-auto p-4 sm:p-6 md:p-8">
        <header className="flex items-center justify-between mb-6">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-800">
            Convidar e Ganhar
          </h1>
          <button
            onClick={() => onVoltar('home')}
            className="bg-violet-600 text-white font-semibold px-4 py-2 rounded-lg hover:bg-violet-700 shadow-sm transition-all duration-300"
          >
            Voltar
          </button>
        </header>

        {/* Styled Alert */}
        {alert.show && (
          <div className={`mb-6 p-4 rounded-lg ${
            alert.type === 'success'
              ? 'bg-green-100 text-green-800 border border-green-200'
              : 'bg-red-100 text-red-800 border border-red-200'
          }`}>
            {alert.message}
          </div>
        )}

        <div className="max-w-4xl mx-auto">
          {/* Seção de Compartilhamento */}
          <div className="bg-white p-6 rounded-xl shadow-md border border-[#E0CCFF] mb-8">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Compartilhe seu código de convite</h2>
            <p className="text-gray-600 mb-4">
              Convide amigos e ganhe créditos quando eles se cadastrarem e forem aprovados!
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 items-center">
              {codigoConvite ? (
                <div className="flex-1 bg-violet-50 p-4 rounded-lg border border-violet-200">
                  <p className="text-sm text-violet-700 mb-1">Seu código de convite:</p>
                  <div className="flex">
                    <input
                      type="text"
                      value={codigoConvite}
                      readOnly
                      className="flex-1 p-3 border border-gray-300 rounded-l-lg bg-gray-100 font-mono"
                    />
                    <button
                      onClick={copiarCodigo}
                      className="bg-violet-600 text-white px-4 py-3 rounded-r-lg hover:bg-violet-700 transition-colors"
                    >
                      Copiar
                    </button>
                  </div>
                </div>
              ) : (
                <div className="flex-1 bg-yellow-50 p-4 rounded-lg border border-yellow-200">
                  <p className="text-sm text-yellow-700 mb-1">Você ainda não tem um código de convite</p>
                  <div className="flex flex-col sm:flex-row gap-2">
                    <button
                      onClick={gerarCodigoConvite}
                      disabled={gerandoCodigo}
                      className={`px-4 py-3 rounded-lg font-semibold transition-colors ${
                        gerandoCodigo
                          ? 'bg-gray-400 text-gray-200 cursor-not-allowed'
                          : 'bg-green-600 text-white hover:bg-green-700'
                      }`}
                    >
                      {gerandoCodigo ? 'Gerando...' : 'Gerar Código de Convite'}
                    </button>
                  </div>
                </div>
              )}
            </div>
            
            <div className="mt-4 p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h3 className="font-semibold text-blue-800 mb-2">Como funciona:</h3>
              <ul className="text-blue-700 list-disc pl-5 space-y-1">
                <li>Compartilhe seu código de convite com amigos</li>
                <li>Seus amigos usam o código ao se cadastrarem</li>
                <li>Quando o administrador aprovar a conta deles, você ganha 1 crédito</li>
                <li>Use seus créditos na Loja de Recompensas</li>
              </ul>
            </div>
          </div>

          {/* Seção de Indicações */}
          <div className="bg-white p-6 rounded-xl shadow-md border border-[#E0CCFF] mb-8">
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-xl font-bold text-gray-800">Suas Indicações</h2>
              <div className="bg-violet-100 text-violet-800 font-bold px-4 py-2 rounded-lg">
                Total: {totalIndicacoes}
              </div>
            </div>

            {indicacoes.length === 0 ? (
              <div className="text-center py-8">
                <p className="text-gray-500">Você ainda não indicou ninguém.</p>
                <p className="text-gray-500 mt-2">Comece a compartilhar seu código de convite!</p>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-violet-100">
                      <th className="p-3 text-left text-violet-800 font-semibold">Nome</th>
                      <th className="p-3 text-left text-violet-800 font-semibold">Email</th>
                      <th className="p-3 text-left text-violet-800 font-semibold">Status</th>
                      <th className="p-3 text-left text-violet-800 font-semibold">Data</th>
                    </tr>
                  </thead>
                  <tbody>
                    {indicacoes.map((indicacao, index) => (
                      <tr key={index} className={index % 2 === 0 ? 'bg-gray-50' : 'bg-white'}>
                        <td className="p-3 border-t border-gray-200">{indicacao.nome}</td>
                        <td className="p-3 border-t border-gray-200">{indicacao.email}</td>
                        <td className="p-3 border-t border-gray-200">
                          <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                            indicacao.status === 'aprovado'
                              ? 'bg-green-100 text-green-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}>
                            {indicacao.status === 'aprovado' ? 'Aprovado' : 'Pendente'}
                          </span>
                        </td>
                        <td className="p-3 border-t border-gray-200">
                          {new Date(indicacao.data).toLocaleDateString('pt-BR')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Seção de Recompensas */}
          <div className="bg-white p-6 rounded-xl shadow-md border border-[#E0CCFF]">
            <h2 className="text-xl font-bold text-gray-800 mb-4">Como Ganhar e Usar Créditos</h2>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="bg-green-50 p-4 rounded-lg border border-green-200">
                <h3 className="font-semibold text-green-800 mb-2">Como Ganhar Créditos</h3>
                <ul className="text-green-700 list-disc pl-5 space-y-1">
                  <li>Ganhe 1 crédito por cada indicação aprovada</li>
                  <li>Seu amigo deve usar seu código de convite ao se cadastrar</li>
                  <li>A conta do seu amigo deve ser aprovada pelo administrador</li>
                </ul>
              </div>
              
              <div className="bg-purple-50 p-4 rounded-lg border border-purple-200">
                <h3 className="font-semibold text-purple-800 mb-2">Como Usar Créditos</h3>
                <ul className="text-purple-700 list-disc pl-5 space-y-1">
                  <li>1 crédito = 7 dias grátis de acesso ao cronograma</li>
                  <li>3 créditos = 30 dias grátis de acesso ao cronograma</li>
                  <li>Os dias são adicionados à sua conta imediatamente</li>
                </ul>
              </div>
            </div>
            
            <div className="mt-6 text-center">
              {user?.isApoiador || user?.isApoiaSeSupporter ? (
                <div className="bg-gradient-to-r from-purple-50 to-blue-50 border border-purple-200 rounded-lg p-6 mb-6">
                  <div className="flex items-center justify-center mb-4">
                    <svg className="w-12 h-12 text-purple-600 mr-3" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.248a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd"></path>
                    </svg>
                    <h3 className="text-xl font-bold text-purple-800">Você é um Apoiador!</h3>
                  </div>
                  <p className="text-purple-700 mb-2">Você tem acesso ilimitado ao cronograma de estudos e não precisa trocar créditos por dias extras.</p>
                  <p className="text-purple-700 mb-4">Mas continue compartilhando seu código de convite para ajudar outros estudantes!</p>
                  <div className="bg-white p-4 rounded-lg border border-purple-100">
                    <p className="text-purple-800 font-semibold">Total de indicações: <span className="text-2xl">{totalIndicacoes}</span></p>
                    <p className="text-sm text-purple-600 mt-1">Cada indicação ajuda outro estudante em sua jornada!</p>
                  </div>
                </div>
              ) : (
                <button
                  onClick={() => onVoltar('loja-de-recompensa')}
                  className="bg-violet-600 text-white font-semibold px-6 py-3 rounded-lg hover:bg-violet-700 transition-colors"
                >
                  Ir para Loja de Recompensas
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default IndiqueGanhePage;